import React, { useEffect, useMemo, useState } from 'react';
import { IconClose, IconPlus } from './Icons';
import { fetchInviteContacts, parseInviteeText, MAX_BULK_INVITES } from '../utils/invites';
import './InviteTeammates.css';

const SOURCE_LABELS = {
  invited: 'You invited',
  invited_you: 'Invited you',
  teammate: 'Teammate',
};

// How many remembered people to show before "Show all".
const SUGGESTION_PREVIEW = 5;

/**
 * InviteTeammates — pick the people to invite: type an address, paste a whole
 * list ("Bulk add"), or tap someone Symphonate remembers (people you invited,
 * who invited you, or share a workspace with — GET /api/me/contacts).
 * Controlled: `value` is the `[{ email, name }]` list, `onChange` gets the next
 * one. Sending is the parent's job (it posts the list to the bulk endpoint, so
 * everyone added here ends up on one team link).
 *
 * `memberEmails` are hidden from suggestions and refused when typed (they're
 * already in); `pendingEmails` stay suggestible but are tagged "Invited" — a
 * new invite replaces their old one. Pass `contacts` to skip the fetch.
 */
export default function InviteTeammates({
  value = [],
  onChange,
  contacts: providedContacts,
  memberEmails = [],
  pendingEmails = [],
  disabled = false,
}) {
  const [loadedContacts, setLoadedContacts] = useState([]);
  const [draft, setDraft] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [error, setError] = useState('');
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (providedContacts) return undefined;
    let cancelled = false;
    fetchInviteContacts().then((list) => {
      if (!cancelled && list.length) setLoadedContacts(list);
    });
    return () => {
      cancelled = true;
    };
  }, [providedContacts]);

  const contacts = providedContacts || loadedContacts;
  const members = useMemo(
    () => new Set(memberEmails.map((e) => String(e || '').toLowerCase())),
    [memberEmails],
  );
  const pending = useMemo(
    () => new Set(pendingEmails.map((e) => String(e || '').toLowerCase())),
    [pendingEmails],
  );
  const added = useMemo(() => new Set(value.map((p) => p.email)), [value]);

  // Merge parsed people into the list. Returns false (with an error shown)
  // when nothing usable came out of the text.
  const addPeople = (people, invalid = []) => {
    const fresh = [];
    const alreadyIn = [];
    for (const p of people) {
      if (members.has(p.email)) alreadyIn.push(p.email);
      else if (!added.has(p.email) && !fresh.some((f) => f.email === p.email)) fresh.push(p);
    }
    const room = MAX_BULK_INVITES - value.length;
    const accepted = fresh.slice(0, Math.max(0, room));
    const notes = [];
    if (invalid.length) notes.push(`Couldn’t read: ${invalid.join(', ')}`);
    if (alreadyIn.length) notes.push(`Already in this workspace: ${alreadyIn.join(', ')}`);
    if (fresh.length > accepted.length)
      notes.push(`You can invite up to ${MAX_BULK_INVITES} at once.`);
    setError(notes.join(' · '));
    if (accepted.length) onChange([...value, ...accepted]);
    return accepted.length > 0 || (invalid.length === 0 && alreadyIn.length === 0);
  };

  const commitDraft = () => {
    if (!draft.trim()) return;
    const { invitees, invalid } = parseInviteeText(draft);
    if (addPeople(invitees, invalid)) setDraft('');
  };

  const commitBulk = () => {
    const { invitees, invalid } = parseInviteeText(bulkText);
    if (invitees.length === 0 && invalid.length === 0) return;
    addPeople(invitees, invalid);
    setBulkText('');
    setBulkOpen(false);
  };

  const remove = (email) => onChange(value.filter((p) => p.email !== email));

  // Suggestions: remembered people not already added or in the workspace,
  // narrowed by whatever is being typed.
  const query = draft.trim().toLowerCase();
  const suggestions = contacts.filter(
    (c) =>
      !added.has(c.email) &&
      !members.has(c.email) &&
      (!query || c.email.includes(query) || (c.name || '').toLowerCase().includes(query)),
  );
  const shownSuggestions = showAll ? suggestions : suggestions.slice(0, SUGGESTION_PREVIEW);

  return (
    <div className="invite-teammates">
      {value.length > 0 && (
        <ul className="invitee-chips" aria-label="People to invite">
          {value.map((p) => (
            <li key={p.email} className="invitee-chip" title={p.email}>
              <span className="invitee-chip-name">{p.name}</span>
              <span className="invitee-chip-email">{p.email}</span>
              <button
                type="button"
                className="invitee-chip-remove"
                onClick={() => remove(p.email)}
                aria-label={`Remove ${p.name}`}
                disabled={disabled}
              >
                <IconClose size={12} />
              </button>
            </li>
          ))}
        </ul>
      )}

      {bulkOpen ? (
        <div className="invite-bulk">
          <textarea
            value={bulkText}
            onChange={(e) => setBulkText(e.target.value)}
            placeholder={
              'Paste emails — one per line or comma-separated\njane@acme.com, Sam Lee <sam@acme.com>'
            }
            rows={4}
            aria-label="Paste a list of emails"
            disabled={disabled}
            autoFocus
          />
          <div className="invite-bulk-actions">
            <button
              type="button"
              className="invite-link-btn"
              onClick={() => {
                setBulkOpen(false);
                setBulkText('');
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="invite-add-btn"
              onClick={commitBulk}
              disabled={disabled || !bulkText.trim()}
            >
              Add to list
            </button>
          </div>
        </div>
      ) : (
        <div className="invite-add-row">
          <input
            type="text"
            inputMode="email"
            value={draft}
            onChange={(e) => {
              setDraft(e.target.value);
              setError('');
            }}
            onKeyDown={(e) => {
              // Enter / comma adds the address instead of submitting the form.
              if (e.key === 'Enter' || e.key === ',') {
                e.preventDefault();
                commitDraft();
              }
            }}
            placeholder="Add a teammate by email…"
            aria-label="Teammate email"
            disabled={disabled}
          />
          <button
            type="button"
            className="invite-add-btn"
            onClick={commitDraft}
            disabled={disabled || !draft.trim()}
          >
            Add
          </button>
          <button
            type="button"
            className="invite-link-btn"
            onClick={() => {
              setBulkOpen(true);
              setError('');
            }}
            disabled={disabled}
          >
            Bulk add
          </button>
        </div>
      )}

      {error && (
        <p className="invite-teammates-error" role="alert">
          {error}
        </p>
      )}

      {suggestions.length > 0 && (
        <div className="invite-suggestions">
          <p className="invite-suggestions-label">People you’ve worked with</p>
          <ul>
            {shownSuggestions.map((c) => (
              <li key={c.email} className="invite-suggestion">
                <span className="invite-suggestion-avatar" aria-hidden="true">
                  {(c.name || c.email).charAt(0).toUpperCase()}
                </span>
                <span className="invite-suggestion-text">
                  <span className="invite-suggestion-name">{c.name}</span>
                  <span className="invite-suggestion-email">{c.email}</span>
                </span>
                <span className="invite-suggestion-source">
                  {pending.has(c.email)
                    ? 'Invited'
                    : (c.sources || []).map((s) => SOURCE_LABELS[s] || s).join(' · ')}
                </span>
                <button
                  type="button"
                  className="invite-suggestion-add"
                  onClick={() => addPeople([{ email: c.email, name: c.name }])}
                  aria-label={`Add ${c.name}`}
                  disabled={disabled}
                >
                  <IconPlus size={12} /> Add
                </button>
              </li>
            ))}
          </ul>
          {suggestions.length > SUGGESTION_PREVIEW && (
            <button type="button" className="invite-link-btn" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Show fewer' : `Show all ${suggestions.length}`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
